import { Form as FactorialForm } from 'factorial-form'
import { computed, makeObservable } from 'mobx'
import moment from 'moment'

import { durationInSeconds } from 'modules/core/lib/duration'
import { EMPTY_TIME_REGEX, TIME_REGEX, convertToSeconds } from 'modules/core/lib/formatTime'
import users from 'modules/core/store/collections/users'

import { PeriodScheduleType } from 'modules/work_schedules/lib/forms/PeriodScheduleTypeForm'
import type DayConfiguration from 'modules/work_schedules/store/models/DayConfiguration'

class DayConfigurationForm extends FactorialForm {
  constructor(values: object) {
    super(values, {
      id: 'number',
      weekday: 'string',
      start_at: 'string',
      end_at: 'string',
      duration_in_seconds: 'number',
      overlap_period_id: 'number',
      duration_in_hours: 'string',
      schedule_type: 'string',
    })

    makeObservable(this, {
      id: computed,
      weekday: computed,
      isValid: computed,
      startAt: computed,
      duration: computed,
      isFixed: computed,
      isOvernight: computed,
      formattedStartAt: computed,
      formattedEndAt: computed,
    })
  }

  get id(): number {
    return this.get('id').value
  }

  get weekday(): string {
    return this.get('weekday').value
  }

  get startAt(): string {
    return this.get('start_at').value
  }

  get isFixed(): boolean {
    return this.get('schedule_type').value === PeriodScheduleType.fixed
  }

  get isEmpty(): boolean {
    if (this.isFixed) {
      return (
        !this.get('start_at').value.match(TIME_REGEX) && !this.get('end_at').value.match(TIME_REGEX)
      )
    }

    return !this.get('duration_in_hours').value.match(TIME_REGEX)
  }

  get isValid(): boolean {
    if (this.isFixed) {
      return (
        !!this.get('start_at').value.match(TIME_REGEX) &&
        !!this.get('end_at').value.match(TIME_REGEX)
      )
    }

    return this.get('duration_in_hours').value.match(TIME_REGEX)
  }

  public clearInput = () => {
    if (this.get('start_at').value.match(EMPTY_TIME_REGEX) && this.get('end_at').value === '') {
      this.get('start_at').set('')
    }

    if (this.get('end_at').value.match(EMPTY_TIME_REGEX) && this.get('start_at').value === '') {
      this.get('end_at').set('')
    }

    if (
      this.get('start_at').value.match(EMPTY_TIME_REGEX) &&
      this.get('end_at').value.match(EMPTY_TIME_REGEX)
    ) {
      this.get('start_at').set('')
      this.get('end_at').set('')
      this.cleanAll()
    }
  }

  get isClean(): boolean {
    return this.get('start_at').value === '' && this.get('start_at').value === ''
  }

  get formattedStartAt() {
    return moment(this.get('start_at').value, 'HH:mm')
  }

  get formattedEndAt() {
    return moment(this.get('end_at').value, 'HH:mm')
  }

  get duration(): number {
    const startAtValue = this.get('start_at').value
    const endAtValue = this.get('end_at').value

    // Covers the Fixed Work Schedule
    if (startAtValue && endAtValue) {
      return durationInSeconds(startAtValue, endAtValue, users.current.isTimeFormat24H)
    }

    return convertToSeconds(this.get('duration_in_hours'))
  }

  get isOvernight(): boolean {
    const startTime = this.get('start_at').value
    const endTime = this.get('end_at').value

    return endTime && startTime && endTime < startTime
  }

  clone(weekday: string): DayConfigurationForm {
    return new DayConfigurationForm({ ...this.serialized, id: null, weekday })
  }

  isMatchWith(dayConfiguration: DayConfiguration): boolean {
    const { id, start_at, weekday, duration_in_seconds } = dayConfiguration.attributes

    return (
      this.id === id ||
      (this.weekday === weekday &&
        (this.startAt === start_at || (this.duration === duration_in_seconds && !this.isFixed)))
    )
  }
}

export default DayConfigurationForm
